import { useEffect, useRef, useState } from 'react'

/**
 * Returns a translateY offset (px) derived from how far a container has travelled
 * through the viewport, scaled by `depth`. Uses rAF-throttled scroll reads and
 * pauses entirely when offscreen or when the user prefers reduced motion.
 */
export function useParallax(depth: number) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    const node = ref.current
    if (!node) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let visible = false
    let frame = 0

    const read = () => {
      frame = 0
      const rect = node.getBoundingClientRect()
      const progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height)
      setOffset((progress - 0.5) * depth * window.innerHeight)
    }
    const onScroll = () => { if (visible && !frame) frame = window.requestAnimationFrame(read) }

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting
      if (visible) read()
    }, { rootMargin: '120px' })

    observer.observe(node)
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [depth])

  return { ref, offset }
}
